import { Base, withScrolledInView } from '@studiometa/js-toolkit';
import { map, clamp01, easeOutQuart } from '@studiometa/js-toolkit/utils';

/**
 *
 */
export default class TeamScrollParent extends withScrolledInView(Base, {}) {
  static config = {
    name: 'TeamScrollParent',
    refs: ['title', 'card[]', 'content', 'clickIndicator'],
    options: {
      offset: { type: String, default: 'start end / end end' },
    },
  };

  isMobile = window.innerWidth < 1024;

  /** @type {number} Current card index shown on top */
  currentCardIndex = 2;

  /** @type {number[]} Rotations for each card */
  cardRotations = [8, -6, 2];

  /**
   * Component mounted.
   */
  mounted() {
    if (this.isMobile) {
      this.setupMobileCards();
    }
  }

  /**
   * Setup mobile cards with initial positions and click handlers.
   */
  setupMobileCards() {
    const cards = /** @type {HTMLElement[]} */ (this.$refs.card);
    cards?.forEach((card, index) => {
      card.style.transform = `translate(-50%, 0) rotate(${this.cardRotations[index]}deg)`;
      card.style.zIndex = String(index + 1);
      card.style.cursor = 'pointer';
      card.style.transition = 'transform 0.3s ease, z-index 0s';

      card.addEventListener('click', () => this.cycleCards());
    });

    const content = /** @type {HTMLElement} */ (this.$refs.content);
    if (content) {
      content.style.opacity = '1';
      content.style.transform = 'none';
    }
  }

  /**
   * Cycle to show the next card on top.
   */
  cycleCards() {
    const cards = /** @type {HTMLElement[]} */ (this.$refs.card);
    if (!cards || cards.length === 0) return;

    const indicator = /** @type {HTMLElement} */ (this.$refs.clickIndicator);
    if (indicator) {
      indicator.style.opacity = '0';
      indicator.style.transition = 'opacity 0.3s ease';
    }

    this.currentCardIndex = (this.currentCardIndex + 1) % cards.length;

    cards.forEach((card, index) => {
      const distance = (index - this.currentCardIndex + cards.length) % cards.length;
      const zIndex = cards.length - distance;
      card.style.zIndex = String(zIndex);

      if (index === this.currentCardIndex) {
        card.style.transform = `translate(-50%, -10px) rotate(${this.cardRotations[index]}deg) scale(1.05)`;
        setTimeout(() => {
          card.style.transform = `translate(-50%, 0) rotate(${this.cardRotations[index]}deg)`;
        }, 150);
      }
    });
  }

  /**
   * Handle resize events.
   * @param {{ width: number }} props Resize service props.
   */
  resized({ width }) {
    const wasMobile = this.isMobile;
    this.isMobile = width < 1024;

    if (!wasMobile && this.isMobile) {
      this.setupMobileCards();
    }
  }

  /**
   * Handle scroll-in-view events.
   * @param {{ dampedProgress: { x: number, y: number } }} props Scroll props.
   */
  scrolledInView({ dampedProgress }) {
    if (this.isMobile) {
      return;
    }

    const progress = dampedProgress.y;

    const title = /** @type {HTMLElement} */ (this.$refs.title);
    if (title) {
      const titleProgress = easeOutQuart(clamp01(map(progress, 0, 0.15, 0, 1)));
      title.style.opacity = String(titleProgress);
      title.style.transform = `scale(${map(titleProgress, 0, 1, 0.8, 1)})`;
    }

    const content = /** @type {HTMLElement} */ (this.$refs.content);
    if (content) {
      const contentProgress = easeOutQuart(clamp01(map(progress, 0.15, 0.35, 0, 1)));
      content.style.opacity = String(contentProgress);
      content.style.transform = `translateY(${map(contentProgress, 0, 1, 30, 0)}px)`;
    }

    const cardConfigs = [
      { range: [0.1, 0.33], rotate: 8 },
      { range: [0.33, 0.66], rotate: -6 },
      { range: [0.66, 1], rotate: 2 },
    ];

    const cards = /** @type {HTMLElement[]} */ (this.$refs.card);
    cards?.forEach((card, index) => {
      const config = cardConfigs[index];
      if (!config) return;

      const [start, end] = config.range;
      const cardProgress = easeOutQuart(clamp01(map(progress, start, end, 0, 1)));

      const xValue =
        index === 0
          ? `calc(${map(cardProgress, 0, 1, 100, 0)}vw - 50%)`
          : index === 1
            ? `calc(${map(cardProgress, 0, 1, -50, 0)}vw - 50%)`
            : '-50%';
      const yValue = index === 2 ? `calc(${map(cardProgress, 0, 1, 100, 0)}vh)` : '0%';

      card.style.transform = [
        `translate(${xValue}, ${yValue})`,
        `scale(${map(cardProgress, 0, 1, 2, 1)})`,
        `rotate(${map(cardProgress, 0, 1, 0, config.rotate)}deg)`,
      ].join(' ');
    });
  }
}
